import React, { useEffect, useRef, useState } from 'react';
import './ListSort.css';

/**
 * Sorting and filtering pieces shared by All tickets and Invoicing.
 *
 *   SortHeader  a <th> whose label is a button. aria-sort on the cell says
 *               which way it is sorted, so a screen reader announces
 *               "Requester, sorted ascending" rather than an arrow glyph.
 *   SortMenu    the same choice for a phone, where there are no headers to
 *               press: one button, a menu of columns, a direction toggle.
 *   Sheet       a bottom sheet for the filters on a phone - a dialog, with
 *               Escape, a backdrop, focus moved in and given back.
 *
 * None of them hold the sort or the filters: the pages keep both in the URL.
 */

const ARROW = {
  asc: <path d="M8 12.5V3.5M4.5 7 8 3.5 11.5 7" />,
  desc: <path d="M8 3.5v9M4.5 9 8 12.5 11.5 9" />,
  none: <path d="M5 6 8 3l3 3M5 10l3 3 3-3" />
};

export function SortHeader({ label, sortKey, sort, dir, onSort, className = '', title }) {
  const on = sort === sortKey;
  const state = on ? dir : 'none';
  return (
    <th
      className={`ls-th ${className}`.trim()}
      aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      data-sort-key={sortKey}
    >
      <button type="button" className={on ? 'ls-sort is-on' : 'ls-sort'}
              onClick={() => onSort(sortKey)}
              title={title || `Sort by ${label.toLowerCase()}`}>
        <span>{label}</span>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
             className="ls-arrow" data-dir={state}>
          {ARROW[state]}
        </svg>
      </button>
    </th>
  );
}

/**
 * The next sort, given a press on `key`: a new column starts in its natural
 * direction (names A-Z, dates and figures newest or biggest first); the same
 * column again flips.
 */
export function nextSort(current, key, firstDir = {}) {
  if (current.sort === key) return { sort: key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { sort: key, dir: firstDir[key] || 'asc' };
}

/** Close a popover on a press outside it or on Escape. */
function useDismiss(open, setOpen, ref, returnTo) {
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); returnTo?.current?.focus(); }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen, ref, returnTo]);
}

export function SortMenu({ options, sort, dir, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);
  const menu = useRef(null);
  useDismiss(open, setOpen, wrap, btn);
  const current = options.find((o) => o.key === sort) || options[0];

  useEffect(() => {
    if (open) menu.current?.querySelector('[aria-checked="true"]')?.focus();
  }, [open]);

  const onMenuKey = (e) => {
    const items = [...(menu.current?.querySelectorAll('[role="menuitemradio"]') || [])];
    const at = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(at + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(at - 1 + items.length) % items.length]?.focus(); }
    if (e.key === 'Home') { e.preventDefault(); items[0]?.focus(); }
    if (e.key === 'End') { e.preventDefault(); items[items.length - 1]?.focus(); }
  };

  return (
    <div className={`ls-menu ${className}`.trim()} ref={wrap}>
      <button type="button" ref={btn} className="ls-menu-btn" aria-haspopup="menu"
              aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 4.5h10M3 8h7M3 11.5h4" />
        </svg>
        <span>Sort: <strong>{current.label}</strong> {dir === 'asc' ? '↑' : '↓'}</span>
      </button>
      {open && (
        <div className="ls-menu-panel" role="menu" aria-label="Sort by" ref={menu}
             onKeyDown={onMenuKey}>
          {options.map((o) => {
            const on = o.key === sort;
            return (
              <button key={o.key} type="button" role="menuitemradio" aria-checked={on}
                      className={on ? 'ls-menu-item is-on' : 'ls-menu-item'}
                      onClick={() => { onChange(on ? { sort: o.key, dir: dir === 'asc' ? 'desc' : 'asc' }
                        : { sort: o.key, dir: o.dir || 'asc' }); setOpen(false); btn.current?.focus(); }}>
                <span>{o.label}</span>
                {on && <span className="ls-menu-dir">{dir === 'asc' ? 'ascending ↑' : 'descending ↓'}</span>}
              </button>
            );
          })}
          <div className="ls-menu-sep" role="separator" />
          <button type="button" role="menuitemradio" aria-checked="false" className="ls-menu-item"
                  onClick={() => { onChange({ sort, dir: dir === 'asc' ? 'desc' : 'asc' }); setOpen(false); btn.current?.focus(); }}>
            <span>Reverse order</span>
            <span className="ls-menu-dir">{dir === 'asc' ? '↓' : '↑'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A bottom sheet. The phone filter panel for both pages.
 */
export function Sheet({ title, open, onClose, children, footer }) {
  const panel = useRef(null);
  const returnTo = useRef(null);
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    returnTo.current = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = panel.current?.querySelector('button, input, select, [tabindex]');
    (first || panel.current)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      // Keep Tab inside the sheet: it is modal.
      const f = [...panel.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!f.length) return;
      const firstEl = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (returnTo.current && returnTo.current.focus) returnTo.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="ls-sheet-backdrop"
         onMouseDown={(e) => { pressedBackdrop.current = e.target === e.currentTarget; }}
         onClick={(e) => { if (pressedBackdrop.current && e.target === e.currentTarget) onClose(); }}>
      <div className="ls-sheet" role="dialog" aria-modal="true" aria-label={title}
           ref={panel} tabIndex={-1}>
        <div className="ls-sheet-grip" aria-hidden="true" />
        <div className="ls-sheet-head">
          <h2>{title}</h2>
          <button type="button" className="ls-sheet-x" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9"
                 strokeLinecap="round" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg>
          </button>
        </div>
        <div className="ls-sheet-body">{children}</div>
        {footer && <div className="ls-sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Whether the viewport is phone-width, tracked live. */
export function usePhone(max = 640) {
  const q = `(max-width: ${max}px)`;
  const get = () => (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(q).matches : false);
  const [phone, setPhone] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = () => setPhone(m.matches);
    on();
    if (m.addEventListener) m.addEventListener('change', on); else m.addListener(on);
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else m.removeListener(on); };
  }, [q]);
  return phone;
}
